"use client";

import { useState } from "react";
import type React from "react";
import Link from "next/link";
import { icons } from "lucide-react";
import type { Category, Article } from "@/types";
import { flattenCategories } from "@/lib/category-tree";

function FolderIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 7a2 2 0 012-2h3.586a1 1 0 01.707.293L10.707 6.7A1 1 0 0011.414 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
      />
    </svg>
  );
}

function toPascalCase(str: string): string {
  return str
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

function CategoryIcon({
  iconName,
  className,
  style,
}: {
  iconName?: string | null;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (iconName) {
    const pascalName = toPascalCase(iconName);
    const iconsMap: Record<
      string,
      React.ComponentType<{ className?: string; style?: React.CSSProperties }>
    > = icons;
    const IconComponent = iconsMap[pascalName];
    if (IconComponent) {
      return <IconComponent className={className} style={style} aria-hidden="true" />;
    }
  }
  return <FolderIcon className={className} style={style} />;
}

interface ArticleListGroupedProps {
  categories: Category[];
  articles: Article[];
}

interface AccordionItemProps {
  category: Category;
  articles: Article[];
  defaultOpen?: boolean;
}

function AccordionItem({ category, articles, defaultOpen = true }: AccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ border: "1px solid #E5E7EB", borderLeft: "3px solid var(--theme-primary)" }}
    >
      <button
        type="button"
        id={`button-${category.id}`}
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 transition-colors duration-200 cursor-pointer"
        style={{
          backgroundColor: isOpen ? "var(--theme-primary-light)" : "white",
        }}
        aria-expanded={isOpen}
        aria-controls={`panel-${category.id}`}
      >
        <span className="flex items-center gap-2">
          <CategoryIcon
            iconName={category.iconName}
            className="h-5 w-5 shrink-0"
            style={{ color: "var(--theme-primary)" }}
          />
          <span className="font-semibold text-[16px] text-[#111827]">{category.name}</span>
        </span>
        <span className="flex items-center gap-2 text-[#6B7280]">
          <span className="text-[13px]">{articles.length}件</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-200"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div id={`panel-${category.id}`} role="region" aria-labelledby={`button-${category.id}`}>
          <ul className="divide-y divide-[#F3F4F6] bg-white">
            {articles.map((article) => (
              <li key={article.id}>
                <Link
                  href={`/articles/${article.slug}`}
                  className="flex items-center px-5 py-3 text-[#374151] hover:text-[var(--theme-primary)] hover:bg-[#F9FAFB] transition-colors duration-200 gap-2"
                  style={{ fontSize: "var(--theme-font-size)" }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span>{article.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * カテゴリ別アコーディオンリスト
 * ワンカラムリストレイアウトのトップページ用
 */
export function ArticleListGrouped({ categories, articles }: ArticleListGroupedProps) {
  // カテゴリ別に記事をグループ化
  // 大カテゴリは記事を持たず中カテゴリにのみ記事が紐づく運用もあるため、
  // ツリーをフラット化して大/中カテゴリ両方をグループ化対象にする
  const grouped = flattenCategories(categories)
    .filter((category) => category.ownArticleCount > 0)
    .map((category) => ({
      category,
      articles: articles.filter((article) => article.category?.id === category.id),
    }))
    .filter(({ articles: categoryArticles }) => categoryArticles.length > 0);

  if (grouped.length === 0) {
    return (
      <div className="py-16 text-center text-[14px] text-[#9CA3AF]">
        <p>記事がありません</p>
        <Link
          href="/"
          className="mt-4 inline-block text-[13px] text-[#6B7280] underline hover:text-[var(--theme-primary)] transition-colors duration-200"
        >
          トップページに戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {grouped.map(({ category, articles: categoryArticles }) => (
        <AccordionItem
          key={category.id}
          category={category}
          articles={categoryArticles}
          defaultOpen={true}
        />
      ))}
    </div>
  );
}
